import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useCreateInteractiveMaterial,
  useGetInteractiveMaterial,
  getInteractiveMaterial,
  getClassroomScopes,
  googleLogin,
  useGetVideosFromGoogleDrive,
  useGetDriveFiles,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import {
  BaseInteractiveMaterialResponseDtoGeneratorMode,
  InteractiveMaterialResponseDtoStatus,
  TeacherDocumentResponseDto,
  DriveFileDto,
  CreateInteractiveMaterialRequestCefrLevel,
} from "@packages/api/madlenAPI.schemas";

import { useQueryClient } from "@tanstack/react-query";

import { Button, TextLinear, Text } from "@core/index";
import { PDFViewer } from "@core/pdf-viewer";
import Pagination from "@core/pagination";
import LoadingContainer from "@packages/components/Loading";
import { useAppDispatch } from "@packages/store/hooks";
import { updateLastUsedTools } from "@packages/store/userSlice";
import GenericFileCard, {
  GenericFileItem,
} from "@features/assignments/AssignmentGenerator/components/GenericFileCard";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";
import esFlag from "@packages/assets/es.png";
import FileUploadModal from "@features/tools/components/file-upload-modal";

const VIDEO_CREATION_STEPS = [
  "interactive_material.steps.validatingUrl",
  "interactive_material.steps.fetchingVideoInfo",
  "interactive_material.steps.creatingMaterial",
  "interactive_material.steps.finalizing",
];

const PDF_CREATION_STEPS = [
  "interactive_material.steps.validatingFile",
  "interactive_material.steps.processingPdf",
  "interactive_material.steps.creatingMaterial",
  "interactive_material.steps.finalizing",
];

export const CreateInteractiveMaterialSubItemSpecsRequestType = {
  VIDEO_QUIZ_TF: "VIDEO_QUIZ_TF",
  VIDEO_QUIZ_TEST: "VIDEO_QUIZ_TEST",
  GLOSSARY: "GLOSSARY",
  FLASHCARD: "FLASHCARD",
  DRAG_DROP: "DRAG_DROP",
  QUIZ_TF: "QUIZ_TF",
  QUIZ_TEST: "QUIZ_TEST",
} as const;

type SubItemType =
  (typeof CreateInteractiveMaterialSubItemSpecsRequestType)[keyof typeof CreateInteractiveMaterialSubItemSpecsRequestType];

interface CreationSpec {
  count: number;
  type: SubItemType;
}

const DEFAULT_CREATION_SPECS: CreationSpec[] = [
  { type: "GLOSSARY", count: 10 },
  { type: "VIDEO_QUIZ_TF", count: 5 },
  { type: "VIDEO_QUIZ_TEST", count: 5 },
  { type: "FLASHCARD", count: 10 },
  { type: "DRAG_DROP", count: 5 },
  { type: "QUIZ_TF", count: 5 },
  { type: "QUIZ_TEST", count: 5 },
];

// CEFR level options
const cefrLevelOptions = [
  { value: CreateInteractiveMaterialRequestCefrLevel.PRE_A1, label: "Pre-A1" },
  { value: CreateInteractiveMaterialRequestCefrLevel.A1, label: "A1" },
  { value: CreateInteractiveMaterialRequestCefrLevel.A2, label: "A2" },
  { value: CreateInteractiveMaterialRequestCefrLevel.B1, label: "B1" },
  { value: CreateInteractiveMaterialRequestCefrLevel.B2, label: "B2" },
  { value: CreateInteractiveMaterialRequestCefrLevel.C1, label: "C1" },
  { value: CreateInteractiveMaterialRequestCefrLevel.C2, label: "C2" },
];

const loginClassroom = async () => {
  try {
    const neededClassroomScopesResponse = await getClassroomScopes();
    const googleLoginRes = await googleLogin({
      scopes: neededClassroomScopesResponse,
      redirectUrl: window.location.href,
    });
    window.location.href = googleLoginRes.redirectUrl;
    return true;
  } catch (e) {
    console.error(e);
  }
};

const CreateInteractiveMaterialView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeId, setYoutubeId] = useState<string | null>(null);
  const [vimeoId, setVimeoId] = useState<string | null>(null);
  const [customVideoId, setCustomVideoId] = useState<string | null>(null);
  const [teacherDocument, setTeacherDocument] =
    useState<TeacherDocumentResponseDto>();
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [newMaterialId, setNewMaterialId] = useState<number | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [creationSpecs, setCreationSpecs] = useState<CreationSpec[]>(
    DEFAULT_CREATION_SPECS,
  );
  const [contentLanguage, setContentLanguage] = useState<
    "en" | "tr" | "de" | "fr" | "es"
  >(i18n.language as "en" | "tr" | "de" | "fr" | "es");
  const [cefrLevel, setCefrLevel] =
    useState<CreateInteractiveMaterialRequestCefrLevel>(
      CreateInteractiveMaterialRequestCefrLevel.A1,
    );
  const [captureOnlyFirstEvents, setCaptureOnlyFirstEvents] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const { mutateAsync: createInteractiveMaterial } =
    useCreateInteractiveMaterial();
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Google Drive related state
  const [didGetDriveFileAccess, setDidGetDriveFileAccess] = useState(false);
  const [selectedDriveFile, setSelectedDriveFile] =
    useState<DriveFileDto | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8);
  const [generatorMode, setGeneratorMode] =
    useState<BaseInteractiveMaterialResponseDtoGeneratorMode>(
      BaseInteractiveMaterialResponseDtoGeneratorMode.YOUTUBE,
    );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const queryClient = useQueryClient();

  // New state for two-level selection
  const [contentType, setContentType] = useState<"video" | "pdf" | null>(null);
  const [videoSource, setVideoSource] = useState<
    "youtube" | "vimeo" | "link" | "drive" | null
  >(null);
  const [pdfSource, setPdfSource] = useState<"upload" | "drive" | null>(null);

  // Search state for Google Drive files
  const [driveSearchTerm, setDriveSearchTerm] = useState("");
  const [thumbnailErrorMap, setThumbnailErrorMap] = useState<
    Record<string | number, boolean>
  >({});

  // Conditional Google Drive hooks based on content type
  const shouldEnablePdfDriveQuery =
    didGetDriveFileAccess && contentType === "pdf" && pdfSource === "drive";
  const shouldEnableVideoDriveQuery =
    didGetDriveFileAccess && contentType === "video" && videoSource === "drive";

  // Hook for PDF files from Google Drive
  const {
    data: pdfDriveFiles,
    error: pdfDriveFilesError,
    refetch: refetchPdfDriveFiles,
    isLoading: isPdfDriveFilesLoading,
  } = useGetDriveFiles(
    {
      page: currentPage,
      size: pageSize,
      ...(driveSearchTerm.trim() && { searchQuery: driveSearchTerm.trim() }),
    },
    {
      query: {
        enabled: shouldEnablePdfDriveQuery,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  // Hook for Video files from Google Drive
  const {
    data: videoDriveFiles,
    error: videoDriveFilesError,
    refetch: refetchVideoDriveFiles,
    isLoading: isVideoDriveFilesLoading,
  } = useGetVideosFromGoogleDrive(
    {
      page: currentPage,
      size: pageSize,
      ...(driveSearchTerm.trim() && { searchQuery: driveSearchTerm.trim() }),
    },
    {
      query: {
        enabled: shouldEnableVideoDriveQuery,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  // Combine the results based on current selection
  const driveFiles = contentType === "pdf" ? pdfDriveFiles : videoDriveFiles;
  const driveFilesError =
    contentType === "pdf" ? pdfDriveFilesError : videoDriveFilesError;
  const isDriveFilesLoading =
    contentType === "pdf" ? isPdfDriveFilesLoading : isVideoDriveFilesLoading;
  const refetchDriveFiles =
    contentType === "pdf" ? refetchPdfDriveFiles : refetchVideoDriveFiles;

  // Enhanced error handling with better retry logic for both hooks
  useEffect(() => {
    if (driveFilesError) {
      console.debug("Drive files error:", driveFilesError);
      console.debug("Error response:", driveFilesError?.response);
      console.debug("Error status:", driveFilesError?.response?.status);

      // Handle authentication errors - 401 (unauthorized) or 404 (not found/no access)
      if (
        driveFilesError?.response?.status === 401 ||
        driveFilesError?.response?.status === 404
      ) {
        console.debug(
          "Authentication error detected, resetting drive access and initiating login",
        );
        setDidGetDriveFileAccess(false);
        setSelectedDriveFile(null);
        setCurrentPage(1);
        loginClassroom();
      }
    }
  }, [pdfDriveFilesError, videoDriveFilesError]);

  useGetInteractiveMaterial(newMaterialId!, {
    query: {
      enabled: !!newMaterialId,
      refetchInterval: (query) => {
        if (
          newMaterialId &&
          query.state.data?.status ===
            InteractiveMaterialResponseDtoStatus.INITIALIZING
        ) {
          return 5000;
        }
        if (
          newMaterialId &&
          query.state.data?.status ===
            InteractiveMaterialResponseDtoStatus.EDITING
        ) {
          navigate(`/content/interactive-materials/${newMaterialId}/edit`);
        }
        return false;
      },
      queryFn: async () => {
        if (newMaterialId === -1) {
          setNewMaterialId(null);
          setIsRunning(false);
          setYoutubeUrl("");
          setYoutubeId(null);
          setVimeoId(null);
          setCustomVideoId(null);
          setGeneratorMode(
            BaseInteractiveMaterialResponseDtoGeneratorMode.YOUTUBE,
          );
          setError(null);
          setUploadError(null);
          setButtonLoading(false);
          setTeacherDocument(undefined);

          addToast({
            title: t("interactive_material.create.error.title"),
            description: t("interactive_material.create.error.description"),
            variant: "error",
            duration: 3500,
          });

          throw new Error("Interactive material creation failed");
        }

        const response = await getInteractiveMaterial(newMaterialId!);
        if (response?.status === InteractiveMaterialResponseDtoStatus.FAILED) {
          setNewMaterialId(null);
          setIsRunning(false);
          addToast({
            title: t("interactive_material.create.error.title"),
            description: t("interactive_material.create.error.description"),
            variant: "error",
            duration: 3500,
          });

          throw new Error("Interactive material creation failed");
        }

        return response;
      },
      retry: false,
    },
  });

  const extractVimeoId = (url: string) => {
    const regExp = /^.*(vimeo\.com\/)(\d+).*/;
    const match = url.match(regExp);
    console.log(match);
    return match && match[2] ? match[2] : null;
  };

  const extractYoutubeId = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Check if URL is a valid custom video URL
  const isValidCustomVideoUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Check if it's HTTP or HTTPS
      if (!["http:", "https:"].includes(urlObj.protocol)) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  // Check if URL has video file extension
  const hasVideoExtension = (url: string): boolean => {
    const videoExtensions = [
      ".mp4",
      ".webm",
      ".ogg",
      ".mov",
      ".avi",
      ".m4v",
      ".3gp",
      ".mkv",
    ];
    const urlWithoutQuery = url.split("?")[0].toLowerCase();
    return videoExtensions.some((ext) => urlWithoutQuery.endsWith(ext));
  };

  // Check if custom video URL is accessible
  const checkVideoAccessibility = async (url: string): Promise<boolean> => {
    try {
      // Use HEAD request to check if resource is accessible without downloading it
      const response = await fetch(url, {
        method: "HEAD",
        mode: "no-cors", // This won't give us detailed info but will prevent CORS errors
      });
      return true; // If no error is thrown, assume it's accessible
    } catch {
      // If CORS prevents us from checking, we'll just try with a basic validation
      return isValidCustomVideoUrl(url);
    }
  };

  const getCreationSteps = () => {
    return generatorMode ===
      BaseInteractiveMaterialResponseDtoGeneratorMode.YOUTUBE
      ? VIDEO_CREATION_STEPS
      : PDF_CREATION_STEPS;
  };

  const handleSubmit = async () => {
    if (
      !youtubeId &&
      !vimeoId &&
      !customVideoId &&
      !teacherDocument &&
      !selectedDriveFile
    )
      return;

    try {
      setButtonLoading(true);

      // Prepare video interval if times are provided
      let videoInterval = undefined;
      if (
        generatorMode ===
          BaseInteractiveMaterialResponseDtoGeneratorMode.YOUTUBE &&
        (startTime || endTime)
      ) {
        const startMs = timeStringToMs(startTime);
        const endMs = timeStringToMs(endTime);

        // Validate that start time is before end time if both are provided
        if (startMs !== null && endMs !== null && startMs >= endMs) {
          addToast({
            title: t("interactive_material.create.error.title"),
            description: t(
              "interactive_material.create.error.invalidTimeRange",
            ),
            variant: "error",
            duration: 3500,
          });
          setButtonLoading(false);
          return;
        }

        // Only create videoInterval object if at least one time is valid
        if (startMs !== null || endMs !== null) {
          videoInterval = {
            ...(startMs !== null && { startMs }),
            ...(endMs !== null && { endMs }),
          };
        }
      }

      const response = await createInteractiveMaterial({
        data: {
          youtubeUrl:
            youtubeId || vimeoId || customVideoId ? youtubeUrl : undefined,
          generatorMode: generatorMode,
          teacherDocumentId: teacherDocument?.id || undefined,
          driveFileId: selectedDriveFile?.id || undefined,
          contentLanguage: contentLanguage,
          cefrLevel: isCheckOutputLanguage(contentLanguage),
          captureStudentsFirstAnswersOnly: captureOnlyFirstEvents,
          videoInterval,
          creationSpecs: creationSpecs.map((spec) => ({
            type: spec.type,
            count: spec.count,
          })),
        },
      });
      dispatch(updateLastUsedTools("interactive-material"));
      setButtonLoading(false);

      setNewMaterialId(response.id);
      setIsRunning(true);
    } catch (error) {
      setNewMaterialId(-1);
    }
  };

  const handleSpecCountChange = (type: SubItemType, count: number) => {
    if (count > 20) return;
    setCreationSpecs((prev) =>
      prev.map((spec) =>
        spec.type === type ? { ...spec, count: Math.max(0, count) } : spec,
      ),
    );
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setYoutubeUrl(url);
    console.log("URL changed to:", url);

    if (url.trim() === "") {
      setYoutubeId(null);
      setVimeoId(null);
      setCustomVideoId(null);
      setError(null);
      return;
    }

    const videoIdYoutube = extractYoutubeId(url);
    console.log("YouTube ID:", videoIdYoutube);
    if (videoIdYoutube) {
      setYoutubeId(videoIdYoutube);
      setVimeoId(null);
      setCustomVideoId(null);
      setError(null);
    } else {
      const videoIdVimeo = extractVimeoId(url);
      console.log("Vimeo ID:", videoIdVimeo);
      if (videoIdVimeo) {
        setVimeoId(videoIdVimeo);
        setYoutubeId(null);
        setCustomVideoId(null);
        setError(null);
      } else {
        // Check if it's a valid custom video URL
        const isValidCustom = isValidCustomVideoUrl(url);
        console.log("Is valid custom URL:", isValidCustom);
        if (isValidCustom) {
          console.log("Setting custom video ID to:", url);
          setCustomVideoId(url);
          setYoutubeId(null);
          setVimeoId(null);
          setError(null);

          // Optional: Check accessibility (non-blocking)
          checkVideoAccessibility(url)
            .then((isAccessible) => {
              if (!isAccessible) {
                setError(
                  t("interactive_material.create.error.videoNotAccessible"),
                );
              }
            })
            .catch(() => {
              // If accessibility check fails, show a warning but don't block
              console.warn(
                "Could not verify video accessibility due to CORS restrictions",
              );
            });

          // Optional: Warn if no video extension (but don't block)
          if (!hasVideoExtension(url)) {
            console.warn("URL doesn't appear to have a video file extension");
          }
        } else {
          setYoutubeId(null);
          setVimeoId(null);
          setCustomVideoId(null);
          setError(t("interactive_material.create.error.invalidUrl"));
        }
      }
    }
  };

  // Helper functions for new two-level selection
  const handleContentTypeChange = (type: "video" | "pdf") => {
    setContentType(type);
    setVideoSource(null);
    setPdfSource(null);

    // Reset all states
    setYoutubeUrl("");
    setYoutubeId(null);
    setVimeoId(null);
    setCustomVideoId(null);
    setStartTime("");
    setEndTime("");
    setTeacherDocument(undefined);
    setSelectedDriveFile(null);
    setDidGetDriveFileAccess(false);
    setCurrentPage(1);
    setError(null);
    setUploadError(null);
    setDriveSearchTerm("");
  };

  const handleVideoSourceChange = (
    source: "youtube" | "vimeo" | "link" | "drive",
  ) => {
    setVideoSource(source);

    // Reset states
    setYoutubeUrl("");
    setYoutubeId(null);
    setVimeoId(null);
    setCustomVideoId(null);
    setStartTime("");
    setEndTime("");
    setSelectedDriveFile(null);
    setError(null);
    setDriveSearchTerm("");
    setGeneratorMode(BaseInteractiveMaterialResponseDtoGeneratorMode.YOUTUBE);
    if (source === "drive") {
      setDidGetDriveFileAccess(true);
      setCurrentPage(1);
    } else {
      setDidGetDriveFileAccess(false);
    }
  };

  const handlePdfSourceChange = (source: "upload" | "drive") => {
    setPdfSource(source);

    setDriveSearchTerm("");
    setGeneratorMode(BaseInteractiveMaterialResponseDtoGeneratorMode.PDF);

    if (source === "drive") {
      setTeacherDocument(undefined);
      setSelectedDriveFile(null);
      setUploadError(null);
      setDidGetDriveFileAccess(true);
      setCurrentPage(1);
    } else {
      setDidGetDriveFileAccess(false);
      setShowUploadModal(true);
    }
  };

  const handleUploadModalClose = () => {
    setShowUploadModal(false);
  };

  const handleUploadModalSuccess = (
    file: File,
    response: TeacherDocumentResponseDto,
  ) => {
    setTeacherDocument(response);
    setUploadError(null);
    setShowUploadModal(false);
  };

  const handleUploadModalError = (error: string) => {
    setUploadError(error);
  };

  // Helper function to convert time string (MM:SS or HH:MM:SS) to milliseconds
  const timeStringToMs = (timeStr: string): number | null => {
    if (!timeStr.trim()) return null;

    const parts = timeStr.split(":").map((part) => parseInt(part, 10));
    if (parts.some(isNaN)) return null;

    if (parts.length === 2) {
      // MM:SS format
      const [minutes, seconds] = parts;
      return (minutes * 60 + seconds) * 1000;
    } else if (parts.length === 3) {
      // HH:MM:SS format
      const [hours, minutes, seconds] = parts;
      return (hours * 3600 + minutes * 60 + seconds) * 1000;
    }

    return null;
  };

  // Helper function to validate time format
  const isValidTimeFormat = (timeStr: string): boolean => {
    if (!timeStr.trim()) return true; // Empty is valid

    const timeRegex = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{1,2})$/;
    const match = timeStr.match(timeRegex);

    if (!match) return false;

    const hours = match[1] ? parseInt(match[1], 10) : 0;
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);

    return minutes < 60 && seconds < 60 && hours >= 0;
  };

  // Handle Google Drive file selection
  const toggleDriveFileSelection = (file: DriveFileDto) => {
    setSelectedDriveFile(selectedDriveFile?.id === file.id ? null : file);
  };

  // Helper function to handle page changes from Pagination component
  const handlePageChange = (page: number) => {
    if (!isDriveFilesLoading) {
      setCurrentPage(page);
    }
  };

  // Helper function to check if form is disabled
  const isFormDisabled = (): boolean => {
    if (buttonLoading) return true;

    // Must select content type first
    if (!contentType) return true;

    if (contentType === "video") {
      // Must select video source
      if (!videoSource) return true;

      if (
        videoSource === "youtube" ||
        videoSource === "vimeo" ||
        videoSource === "link"
      ) {
        const hasVideoUrl = youtubeId || vimeoId || customVideoId;
        if (!hasVideoUrl) return true;

        // Check for invalid time formats
        if (startTime.trim() !== "" && !isValidTimeFormat(startTime))
          return true;
        if (endTime.trim() !== "" && !isValidTimeFormat(endTime)) return true;
      } else if (videoSource === "drive") {
        if (!selectedDriveFile) return true;
      }
    } else if (contentType === "pdf") {
      // Must select PDF source
      if (!pdfSource) return true;

      if (pdfSource === "upload") {
        if (!teacherDocument?.id) return true;
      } else if (pdfSource === "drive") {
        if (!selectedDriveFile) return true;
      }
    }

    return false;
  };

  // Helper function to handle search changes
  const handleSearchChange = (searchTerm: string) => {
    setDriveSearchTerm(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
    setSelectedDriveFile(null); // Clear selection when searching
  };

  const isCheckOutputLanguage = (language: string) => {
    return ["en", "fr", "de", "es"].includes(language)
      ? cefrLevel
      : CreateInteractiveMaterialRequestCefrLevel.A1;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rose-50 dark:from-gray-900 dark:to-rose-900">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center mb-8 sm:mb-0">
          <Button
            variant="solid"
            size="icon"
            className="px-4 py-2"
            rounded="lg"
            bgColor="bg-rose-500 hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-700"
            textColor="text-white dark:text-white"
            leftIcon={<ArrowLeft size={24} />}
            onClick={() => navigate("/content/interactive-materials")}
          />
        </div>

        {/* Hero Section */}
        <div className="text-center mb-8 sm:mb-12">
          <TextLinear
            variant={"heroTitle"}
            from="from-rose-600 dark:from-rose-400"
            to="to-pink-600 dark:to-pink-400"
            className="mb-4 text-3xl sm:text-4xl md:text-5xl font-bold"
          >
            {t("quickActions.interactiveMaterial")}
          </TextLinear>
          <Text
            variant={"p"}
            status="subtle"
            size={"lg"}
            className="max-w-4xl mx-auto"
          >
            {t("quickActions.interactiveMaterialDesc")}
          </Text>
        </div>

        {/* Supported Platforms Section */}
        <div className="text-center mb-8 sm:mb-12">
          <Text variant="p" size="sm" status="subtle" className="mb-4">
            {t("interactive_material.create.supportedPlatforms")}
          </Text>
          <div className="flex items-center justify-center gap-6 sm:gap-4">
            {/* YouTube */}
            <div className="flex flex-col items-center gap-2 w-16">
              <div className="w-8 h-8 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </div>
              <Text
                variant="span"
                size="xs"
                status="subtle"
                className="text-center leading-tight"
              >
                YouTube
              </Text>
            </div>

            {/* Vimeo */}
            <div className="flex flex-col items-center gap-2 w-16">
              <div className="w-8 h-8 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 4.926-4.12C6.4 1.608 7.263.65 7.263.65s1.4-.875 3.094-.875c1.59 0 2.729 1.132 3.389 3.881.659 2.748 1.102 4.43 1.329 5.047.659 3.881 1.132 5.813 1.416 5.813.38 0 .946-.594 1.699-1.782.754-1.189 1.132-2.096 1.132-2.722 0-1.294-.378-1.941-1.132-1.941-.379 0-.852.189-1.416.567 1.416-4.64 4.12-6.896 8.114-6.766 2.958.094 4.353 2.007 4.188 5.732z" />
                </svg>
              </div>
              <Text
                variant="span"
                size="xs"
                status="subtle"
                className="text-center leading-tight"
              >
                Vimeo
              </Text>
            </div>

            {/* Google Drive */}
            <div className="flex flex-col items-center gap-2 w-16">
              <div className="w-8 h-8 flex items-center justify-center">
                <svg
                  className="w-8 h-8"
                  viewBox="0 0 87.3 78"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                    fill="#0066da"
                  />
                  <path
                    d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                    fill="#00ac47"
                  />
                  <path
                    d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                    fill="#ea4335"
                  />
                  <path
                    d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                    fill="#00832d"
                  />
                  <path
                    d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                    fill="#2684fc"
                  />
                  <path
                    d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                    fill="#ffba00"
                  />
                </svg>
              </div>
              <Text
                variant="span"
                size="xs"
                status="subtle"
                className="text-center leading-tight"
              >
                Drive
              </Text>
            </div>

            {/* Custom Links */}
            <div className="flex flex-col items-center gap-2 w-16">
              <div className="w-8 h-8 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-600 dark:text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>
              <Text
                variant="span"
                size="xs"
                status="subtle"
                className="text-center leading-tight"
              >
                Links
              </Text>
            </div>
          </div>
        </div>

        {/* Input Form Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-200/50 dark:border-gray-700/50">
            {!isRunning ? (
              <div className="space-y-6">
                {/* Two-Level Content Selection */}
                <div className="space-y-6">
                  {/* Step 1: Content Type Selection */}
                  <div className="space-y-3">
                    <Text
                      variant="span"
                      className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                    >
                      {t("interactive_material.create.contentType")}
                    </Text>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => handleContentTypeChange("video")}
                        rounded="xl"
                        bgColor={
                          contentType === "video"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-4 border-2 ${
                          contentType === "video"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {t(
                              "interactive_material.create.videoType",
                              "Video",
                            )}
                          </span>
                        </div>
                      </Button>

                      <Button
                        onClick={() => handleContentTypeChange("pdf")}
                        rounded="xl"
                        bgColor={
                          contentType === "pdf"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-4 border-2 ${
                          contentType === "pdf"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {t("interactive_material.create.pdfType", "PDF")}
                          </span>
                        </div>
                      </Button>
                    </div>
                  </div>

                  {/* Step 2: Platform Selection (appears after content type selection) */}
                  {contentType === "video" && (
                    <div className="space-y-3 animate-fadeIn">
                      <Text
                        variant="span"
                        className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t("interactive_material.create.selectVideoSource")}
                      </Text>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          onClick={() => handleVideoSourceChange("youtube")}
                          rounded="lg"
                          bgColor={
                            videoSource === "youtube"
                              ? "bg-red-50 dark:bg-red-900/20"
                              : ""
                          }
                          className={`p-3 border-2 ${
                            videoSource === "youtube"
                              ? "border-red-500"
                              : "border-gray-200 hover:border-red-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <svg
                              className="w-5 h-5 text-red-600"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                            <span className="text-xs">YouTube</span>
                          </div>
                        </Button>

                        <Button
                          onClick={() => handleVideoSourceChange("vimeo")}
                          rounded="lg"
                          bgColor={
                            videoSource === "vimeo"
                              ? "bg-blue-50 dark:bg-blue-900/20"
                              : ""
                          }
                          className={`p-3 border-2 ${
                            videoSource === "vimeo"
                              ? "border-blue-500"
                              : "border-gray-200 hover:border-blue-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <svg
                              className="w-5 h-5 text-blue-500"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 4.926-4.12C6.4 1.608 7.263.65 7.263.65s1.4-.875 3.094-.875c1.59 0 2.729 1.132 3.389 3.881.659 2.748 1.102 4.43 1.329 5.047.659 3.881 1.132 5.813 1.416 5.813.38 0 .946-.594 1.699-1.782.754-1.189 1.132-2.096 1.132-2.722 0-1.294-.378-1.941-1.132-1.941-.379 0-.852.189-1.416.567 1.416-4.64 4.12-6.896 8.114-6.766 2.958.094 4.353 2.007 4.188 5.732z" />
                            </svg>
                            <span className="text-xs">Vimeo</span>
                          </div>
                        </Button>

                        <Button
                          onClick={() => handleVideoSourceChange("link")}
                          rounded="lg"
                          bgColor={
                            videoSource === "link"
                              ? "bg-gray-50 dark:bg-gray-900/20"
                              : ""
                          }
                          className={`p-3 border-2 ${
                            videoSource === "link"
                              ? "border-gray-500"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                            <span className="text-xs">Link</span>
                          </div>
                        </Button>

                        <Button
                          onClick={() => handleVideoSourceChange("drive")}
                          rounded="lg"
                          bgColor={
                            videoSource === "drive"
                              ? "bg-green-50 dark:bg-green-900/20"
                              : ""
                          }
                          className={`p-3 border-2 ${
                            videoSource === "drive"
                              ? "border-green-500"
                              : "border-gray-200 hover:border-green-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                              />
                            </svg>
                            <span className="text-xs">Drive</span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Video URL Input - Moved up and enhanced for better visibility */}
                  {contentType === "video" &&
                    videoSource &&
                    (videoSource === "youtube" ||
                      videoSource === "vimeo" ||
                      videoSource === "link") && (
                      <div className="space-y-4 animate-fadeIn">
                        {/* Eye-catching header with gradient background */}
                        <div className="bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 rounded-xl p-4 border-2 border-rose-200 dark:border-rose-700 shadow-lg">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                              <svg
                                className="w-5 h-5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                />
                              </svg>
                            </div>
                            <div>
                              <Text
                                variant="span"
                                className="block text-lg font-bold text-rose-700 dark:text-rose-300"
                              >
                                {videoSource === "youtube" &&
                                  t(
                                    "interactive_material.create.youtubeUrl",
                                    "YouTube URL",
                                  )}
                                {videoSource === "vimeo" &&
                                  t(
                                    "interactive_material.create.vimeoUrl",
                                    "Vimeo URL",
                                  )}
                                {videoSource === "link" &&
                                  t(
                                    "interactive_material.create.videoUrl",
                                    "Video URL",
                                  )}
                              </Text>
                              <Text
                                variant="span"
                                className="text-sm text-rose-600 dark:text-rose-400 font-medium"
                              >
                                {t(
                                  "interactive_material.create.pasteUrlHere",
                                  "Paste your video link here",
                                )}
                              </Text>
                            </div>
                          </div>

                          {/* Enhanced input with prominent styling */}
                          <div className="relative">
                            <input
                              type="text"
                              value={youtubeUrl}
                              onChange={handleUrlChange}
                              className="w-full rounded-xl border-3 border-rose-300 dark:border-rose-600 bg-white dark:bg-gray-800 p-4 text-base sm:text-lg font-medium placeholder-rose-400 dark:placeholder-rose-500 focus:ring-4 focus:ring-rose-500/50 focus:border-rose-500 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01]"
                              placeholder={
                                videoSource === "youtube"
                                  ? "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                                  : videoSource === "vimeo"
                                    ? "https://vimeo.com/123456789"
                                    : "https://example.com/video.mp4"
                              }
                            />

                            {/* URL validation indicator */}
                            {youtubeUrl &&
                              (youtubeId || vimeoId || customVideoId) && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <svg
                                      className="w-4 h-4 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              )}
                          </div>

                          {error && (
                            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                              <Text
                                status="error"
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                {error}
                              </Text>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {contentType === "pdf" && (
                    <div className="space-y-3 animate-fadeIn">
                      <Text
                        variant="span"
                        className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t("interactive_material.create.selectPdfSource")}
                      </Text>
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          onClick={() => handlePdfSourceChange("upload")}
                          rounded="lg"
                          bgColor={
                            pdfSource === "upload"
                              ? "bg-blue-50 dark:bg-blue-900/20"
                              : ""
                          }
                          className={`p-4 border-2 ${
                            pdfSource === "upload"
                              ? "border-blue-500"
                              : "border-gray-200 hover:border-blue-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                              />
                            </svg>
                            <span className="text-sm font-medium">
                              {t("interactive_material.create.uploadPdf")}
                            </span>
                          </div>
                        </Button>

                        <Button
                          onClick={() => handlePdfSourceChange("drive")}
                          rounded="lg"
                          bgColor={
                            pdfSource === "drive"
                              ? "bg-green-50 dark:bg-green-900/20"
                              : ""
                          }
                          className={`p-4 border-2 ${
                            pdfSource === "drive"
                              ? "border-green-500"
                              : "border-gray-200 hover:border-green-400"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <svg className="w-6 h-6" viewBox="0 0 24 24">
                              <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                              />
                            </svg>
                            <span className="text-sm font-medium">
                              {t(
                                "interactive_material.create.driveType",
                                "Google Drive",
                              )}
                            </span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Language Selection */}
                  <div className="space-y-3">
                    <Text
                      variant="span"
                      className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                    >
                      {t("interactive_material.create.contentLanguage")}
                    </Text>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                      <Button
                        onClick={() => setContentLanguage("en")}
                        rounded="xl"
                        bgColor={
                          contentLanguage === "en"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-2 sm:p-3 border-2 ${
                          contentLanguage === "en"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={enFlag}
                            alt="English"
                            className="w-4 h-4 rounded-full object-cover shadow-sm"
                          />
                          <span className="text-sm sm:text-base">English</span>
                        </div>
                      </Button>

                      <Button
                        onClick={() => setContentLanguage("tr")}
                        rounded="xl"
                        bgColor={
                          contentLanguage === "tr"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-2 sm:p-3 border-2 ${
                          contentLanguage === "tr"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={trFlag}
                            alt="Turkish"
                            className="w-4 h-4 rounded-full object-cover shadow-sm"
                          />
                          <span className="text-sm sm:text-base">Türkçe</span>
                        </div>
                      </Button>
                      <Button
                        onClick={() => setContentLanguage("de")}
                        rounded="xl"
                        bgColor={
                          contentLanguage === "de"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-2 sm:p-3 border-2 ${
                          contentLanguage === "de"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={deFlag}
                            alt="German"
                            className="w-4 h-4 rounded-full object-cover shadow-sm"
                          />
                          <span className="text-sm sm:text-base">Deutsch</span>
                        </div>
                      </Button>
                      <Button
                        onClick={() => setContentLanguage("fr")}
                        rounded="xl"
                        bgColor={
                          contentLanguage === "fr"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-2 sm:p-3 border-2 ${
                          contentLanguage === "fr"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={frFlag}
                            alt="French"
                            className="w-4 h-4 rounded-full object-cover shadow-sm"
                          />
                          <span className="text-sm sm:text-base">Français</span>
                        </div>
                      </Button>
                      <Button
                        onClick={() => setContentLanguage("es")}
                        rounded="xl"
                        bgColor={
                          contentLanguage === "es"
                            ? "bg-rose-50 dark:bg-rose-900/20"
                            : ""
                        }
                        className={`p-2 sm:p-3 border-2 ${
                          contentLanguage === "es"
                            ? "border-rose-500"
                            : "border-gray-200 hover:border-rose-400"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <img
                            src={esFlag}
                            alt="Spanish"
                            className="w-4 h-4 rounded-full object-cover shadow-sm"
                          />
                          <span className="text-sm sm:text-base">Español</span>
                        </div>
                      </Button>
                    </div>
                  </div>

                  {/* CEFR Level Selector - Only show for English */}
                  {["en", "fr", "de", "es"].includes(contentLanguage) && (
                    <div className="space-y-3 animate-fadeIn">
                      <Text
                        variant="span"
                        className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                      >
                        {t("interactive_material.cefrLevel")}
                      </Text>
                      <div className="space-y-2">
                        {/* First row: 4 levels (Pre-A1, A1, A2, B1) */}
                        <div className="grid grid-cols-4 gap-2">
                          {cefrLevelOptions.slice(0, 4).map((option) => (
                            <Button
                              key={option.value}
                              onClick={() => setCefrLevel(option.value)}
                              rounded="xl"
                              bgColor={
                                cefrLevel === option.value
                                  ? "bg-rose-50 dark:bg-rose-900/20"
                                  : ""
                              }
                              className={`p-2 sm:p-3 border-2 ${
                                cefrLevel === option.value
                                  ? "border-rose-500"
                                  : "border-gray-200 hover:border-rose-400"
                              }`}
                            >
                              <div className="flex items-center justify-center">
                                <span className="text-sm sm:text-base font-medium">
                                  {option.label}
                                </span>
                              </div>
                            </Button>
                          ))}
                        </div>
                        {/* Second row: 3 levels (B2, C1, C2) */}
                        <div className="grid grid-cols-3 gap-2">
                          {cefrLevelOptions.slice(4).map((option) => (
                            <Button
                              key={option.value}
                              onClick={() => setCefrLevel(option.value)}
                              rounded="xl"
                              bgColor={
                                cefrLevel === option.value
                                  ? "bg-rose-50 dark:bg-rose-900/20"
                                  : ""
                              }
                              className={`p-2 sm:p-3 border-2 ${
                                cefrLevel === option.value
                                  ? "border-rose-500"
                                  : "border-gray-200 hover:border-rose-400"
                              }`}
                            >
                              <div className="flex items-center justify-center">
                                <span className="text-sm sm:text-base font-medium">
                                  {option.label}
                                </span>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Input Section */}
                {contentType === "video" && videoSource && (
                  <div className="space-y-3 animate-fadeIn">
                    {videoSource === "drive" && (
                      <div className="space-y-3">
                        {/* Google Drive Files Section */}
                        <div className="mt-6">
                          <Text
                            variant="span"
                            className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-3"
                          >
                            {t("interactive_material.create.selectDriveFile")}
                          </Text>

                          {/* Search Input */}
                          <div className="mb-4">
                            <div className="relative">
                              <svg
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                              <input
                                type="text"
                                value={driveSearchTerm}
                                onChange={(e) =>
                                  handleSearchChange(e.target.value)
                                }
                                className="w-full pl-10 pr-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200"
                                placeholder={t(
                                  "interactive_material.create.searchFiles",
                                  "Search files...",
                                )}
                              />
                              {driveSearchTerm && (
                                <button
                                  onClick={() => handleSearchChange("")}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Enhanced error state handling */}
                          {driveFilesError && !isDriveFilesLoading && (
                            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                              <div className="flex items-center">
                                <svg
                                  className="w-5 h-5 text-red-500 mr-2"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <Text
                                  variant="p"
                                  size="sm"
                                  className="text-red-800 dark:text-red-200"
                                >
                                  {driveFilesError?.response?.status === 401 ||
                                  driveFilesError?.response?.status === 404
                                    ? t(
                                        "interactive_material.create.driveAuthError",
                                      )
                                    : t(
                                        "interactive_material.create.driveLoadError",
                                      )}
                                </Text>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                rounded="lg"
                                className="mt-2"
                                onClick={() => refetchDriveFiles()}
                              >
                                {t("common.retry")}
                              </Button>
                            </div>
                          )}

                          {isDriveFilesLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
                              <Text
                                variant="p"
                                size="sm"
                                className="ml-3 text-gray-600 dark:text-gray-300"
                              >
                                {t("interactive_material.create.loadingFiles")}
                              </Text>
                            </div>
                          ) : driveFiles?.data && driveFiles.data.length > 0 ? (
                            <>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                {isDriveFilesLoading &&
                                  Array.from({ length: pageSize }).map(
                                    (_, index) => (
                                      <GenericFileCard
                                        key={`skeleton-drive-${index}`}
                                        isLoading={true}
                                      />
                                    ),
                                  )}
                                {!isDriveFilesLoading &&
                                  driveFiles.data?.map((file) => {
                                    const fileItem: GenericFileItem = {
                                      id: file.id!,
                                      title: file.title,
                                      thumbnailUrl: file.thumbnailUrl,
                                      alternateLink: file.alternateLink,
                                    };
                                    return (
                                      <GenericFileCard
                                        key={file.id}
                                        file={fileItem}
                                        isSelected={
                                          selectedDriveFile?.id === file.id
                                        }
                                        onSelect={() =>
                                          toggleDriveFileSelection(file)
                                        }
                                        thumbnailError={
                                          thumbnailErrorMap[file.id!] || false
                                        }
                                        onThumbnailError={(
                                          fileId: string | number,
                                        ) =>
                                          setThumbnailErrorMap((prevMap) => ({
                                            ...prevMap,
                                            [fileId]: true,
                                          }))
                                        }
                                      />
                                    );
                                  })}
                              </div>

                              {/* Enhanced Pagination Controls */}
                              <Pagination
                                currentPage={currentPage}
                                hasNextPage={driveFiles.hasNextPage}
                                onPageChange={handlePageChange}
                              />
                            </>
                          ) : driveFiles?.data &&
                            driveFiles.data.length === 0 &&
                            !driveFilesError ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                              <svg
                                className="w-12 h-12 text-gray-400 mb-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <Text
                                variant="p"
                                className="text-gray-600 dark:text-gray-300 mb-1"
                              >
                                {t("interactive_material.create.noFilesFound")}
                              </Text>
                              <Text variant="p" size="sm" status="subtle">
                                {t(
                                  "interactive_material.create.noFilesFoundDesc",
                                )}
                              </Text>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {contentType === "pdf" && pdfSource && (
                  <div className="space-y-3 animate-fadeIn">
                    {pdfSource === "upload" && (
                      <>
                        {uploadError && (
                          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                            <Text
                              variant="p"
                              status="error"
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              {uploadError}
                            </Text>
                          </div>
                        )}
                      </>
                    )}

                    {pdfSource === "drive" && (
                      <div className="space-y-3">
                        {/* Google Drive Files Section for PDF */}
                        <div className="mt-6">
                          <Text
                            variant="span"
                            className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-3"
                          >
                            {t("interactive_material.create.selectDriveFile")}
                          </Text>

                          {/* Search Input */}
                          <div className="mb-4">
                            <div className="relative">
                              <svg
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                              <input
                                type="text"
                                value={driveSearchTerm}
                                onChange={(e) =>
                                  handleSearchChange(e.target.value)
                                }
                                className="w-full pl-10 pr-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200"
                                placeholder={t(
                                  "interactive_material.create.searchFiles",
                                  "Search files...",
                                )}
                              />
                              {driveSearchTerm && (
                                <button
                                  onClick={() => handleSearchChange("")}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Enhanced error state handling */}
                          {driveFilesError && !isDriveFilesLoading && (
                            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                              <div className="flex items-center">
                                <svg
                                  className="w-5 h-5 text-red-500 mr-2"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <Text
                                  variant="p"
                                  size="sm"
                                  className="text-red-800 dark:text-red-200"
                                >
                                  {driveFilesError?.response?.status === 401 ||
                                  driveFilesError?.response?.status === 404
                                    ? t(
                                        "interactive_material.create.driveAuthError",
                                      )
                                    : t(
                                        "interactive_material.create.driveLoadError",
                                      )}
                                </Text>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                rounded="lg"
                                className="mt-2"
                                onClick={() => refetchDriveFiles()}
                              >
                                {t("common.retry")}
                              </Button>
                            </div>
                          )}

                          {isDriveFilesLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-500"></div>
                              <Text
                                variant="p"
                                size="sm"
                                className="ml-3 text-gray-600 dark:text-gray-300"
                              >
                                {t("interactive_material.create.loadingFiles")}
                              </Text>
                            </div>
                          ) : driveFiles?.data && driveFiles.data.length > 0 ? (
                            <>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                {isDriveFilesLoading &&
                                  Array.from({ length: pageSize }).map(
                                    (_, index) => (
                                      <GenericFileCard
                                        key={`skeleton-drive-${index}`}
                                        isLoading={true}
                                      />
                                    ),
                                  )}
                                {!isDriveFilesLoading &&
                                  driveFiles.data?.map((file) => {
                                    const fileItem: GenericFileItem = {
                                      id: file.id!,
                                      title: file.title,
                                      thumbnailUrl: file.thumbnailUrl,
                                      alternateLink: file.alternateLink,
                                    };
                                    return (
                                      <GenericFileCard
                                        key={file.id}
                                        file={fileItem}
                                        isSelected={
                                          selectedDriveFile?.id === file.id
                                        }
                                        onSelect={() =>
                                          toggleDriveFileSelection(file)
                                        }
                                        thumbnailError={
                                          thumbnailErrorMap[file.id!] || false
                                        }
                                        onThumbnailError={(
                                          fileId: string | number,
                                        ) =>
                                          setThumbnailErrorMap((prevMap) => ({
                                            ...prevMap,
                                            [fileId]: true,
                                          }))
                                        }
                                      />
                                    );
                                  })}
                              </div>

                              {/* Enhanced Pagination Controls */}
                              <Pagination
                                currentPage={currentPage}
                                hasNextPage={driveFiles.hasNextPage}
                                onPageChange={handlePageChange}
                              />
                            </>
                          ) : driveFiles?.data &&
                            driveFiles.data.length === 0 &&
                            !driveFilesError ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                              <svg
                                className="w-12 h-12 text-gray-400 mb-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <Text
                                variant="p"
                                className="text-gray-600 dark:text-gray-300 mb-1"
                              >
                                {t("interactive_material.create.noFilesFound")}
                              </Text>
                              <Text variant="p" size="sm" status="subtle">
                                {t(
                                  "interactive_material.create.noFilesFoundDesc",
                                )}
                              </Text>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Preview Section */}
                {(youtubeId ||
                  vimeoId ||
                  customVideoId ||
                  (contentType === "pdf" && teacherDocument?.id)) && (
                  <div className="space-y-3">
                    <Text
                      variant="span"
                      className="block text-sm font-medium text-gray-600 dark:text-gray-300"
                    >
                      {t("interactive_material.create.preview")}
                    </Text>
                    {contentType === "pdf" && teacherDocument?.downloadUrl ? (
                      <div className="h-[300px] sm:h-[400px] rounded-xl overflow-hidden">
                        <PDFViewer url={teacherDocument.downloadUrl} />
                      </div>
                    ) : youtubeId ? (
                      <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                        <iframe
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                    ) : vimeoId ? (
                      <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                        <iframe
                          src={`https://player.vimeo.com/video/${vimeoId}`}
                          title="Vimeo video player"
                          allow="autoplay; fullscreen; picture-in-picture"
                          className="w-full h-full"
                        />
                      </div>
                    ) : customVideoId ? (
                      <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                        <video
                          src={customVideoId}
                          title="Custom video player"
                          controls
                          className="w-full h-full object-contain"
                          preload="metadata"
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Advanced Options Section */}
                <div className="space-y-4">
                  <button
                    onClick={() => setShowAdvancedOptions((prev) => !prev)}
                    className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-rose-400 dark:hover:border-rose-500 transition-all duration-200"
                  >
                    <span>
                      {t("interactive_material.create.advancedOptions")}
                    </span>
                    {showAdvancedOptions ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>

                  {showAdvancedOptions && (
                    <div className="space-y-4 p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 animate-fadeIn">
                      <Text
                        variant="p"
                        size="sm"
                        status="subtle"
                        className="mb-3 sm:mb-4"
                      >
                        {t(
                          "interactive_material.create.advancedOptionsDescription",
                        )}
                      </Text>

                      {/* Capture Only First Events Checkbox */}
                      <div className="flex items-center space-x-2 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-4">
                        <input
                          type="checkbox"
                          id="captureOnlyFirstEvents"
                          checked={captureOnlyFirstEvents}
                          onChange={(e) =>
                            setCaptureOnlyFirstEvents(e.target.checked)
                          }
                          className="h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-rose-600"
                        />
                        <label
                          htmlFor="captureOnlyFirstEvents"
                          className="text-sm font-medium text-gray-700 dark:text-gray-300 select-none"
                        >
                          {t(
                            "interactive_material.create.captureOnlyFirstEvents",
                          )}
                        </label>
                      </div>

                      {/* Time Interval Selection for Videos */}
                      {contentType === "video" &&
                        (youtubeId ||
                          vimeoId ||
                          customVideoId ||
                          (videoSource === "drive" && selectedDriveFile)) && (
                          <div className="space-y-3 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-4">
                            <div className="flex items-center gap-2">
                              <Clock className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                              <Text
                                variant="span"
                                className="text-sm font-medium text-gray-600 dark:text-gray-300"
                              >
                                {t("interactive_material.create.timeInterval")}{" "}
                                ({t("interactive_material.create.optional")})
                              </Text>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Text
                                  variant="span"
                                  className="block text-xs font-medium text-gray-500 dark:text-gray-400"
                                >
                                  {t("interactive_material.create.startTime")}
                                </Text>
                                <input
                                  type="text"
                                  value={startTime}
                                  onChange={(e) => setStartTime(e.target.value)}
                                  className={`w-full rounded-lg border-2 p-2 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 ${
                                    startTime && !isValidTimeFormat(startTime)
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-200 dark:border-gray-600"
                                  }`}
                                  placeholder={t(
                                    "interactive_material.create.startTimePlaceholder",
                                  )}
                                />
                                {startTime && !isValidTimeFormat(startTime) && (
                                  <Text status="error" size="xs">
                                    {t(
                                      "interactive_material.create.error.invalidTimeFormat",
                                    )}
                                  </Text>
                                )}
                              </div>

                              <div className="space-y-2">
                                <Text
                                  variant="span"
                                  className="block text-xs font-medium text-gray-500 dark:text-gray-400"
                                >
                                  {t("interactive_material.create.endTime")}
                                </Text>
                                <input
                                  type="text"
                                  value={endTime}
                                  onChange={(e) => setEndTime(e.target.value)}
                                  className={`w-full rounded-lg border-2 p-2 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 ${
                                    endTime && !isValidTimeFormat(endTime)
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-gray-200 dark:border-gray-600"
                                  }`}
                                  placeholder={t(
                                    "interactive_material.create.endTimePlaceholder",
                                  )}
                                />
                                {endTime && !isValidTimeFormat(endTime) && (
                                  <Text status="error" size="xs">
                                    {t(
                                      "interactive_material.create.error.invalidTimeFormat",
                                    )}
                                  </Text>
                                )}
                              </div>
                            </div>

                            <Text
                              variant="p"
                              size="xs"
                              status="subtle"
                              className="mt-2"
                            >
                              {t(
                                "interactive_material.create.timeIntervalDescription",
                              )}
                            </Text>
                          </div>
                        )}

                      {/* Creation Specs */}
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {creationSpecs
                            .filter(
                              (spec) =>
                                (contentType === "pdf" &&
                                  !spec.type.startsWith("VIDEO_QUIZ")) ||
                                contentType === "video",
                            )
                            .map((spec) => (
                              <div
                                key={spec.type}
                                className={`flex flex-col space-y-2 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 justify-center items-center ${
                                  spec.type === "GLOSSARY"
                                    ? "sm:col-span-2"
                                    : ""
                                }`}
                              >
                                <Text
                                  variant="span"
                                  size="sm"
                                  className="font-medium text-gray-700 dark:text-gray-300 text-center"
                                >
                                  {t(
                                    `interactive_material.types.${spec.type.toLowerCase()}`,
                                  )}
                                </Text>
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() =>
                                      handleSpecCountChange(
                                        spec.type,
                                        spec.count - 1,
                                      )
                                    }
                                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-rose-100 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 transition-colors touch-manipulation"
                                  >
                                    <span className="text-lg font-medium">
                                      -
                                    </span>
                                  </button>
                                  <input
                                    type="number"
                                    value={spec.count}
                                    max={20}
                                    onChange={(e) =>
                                      handleSpecCountChange(
                                        spec.type,
                                        parseInt(e.target.value) || 0,
                                      )
                                    }
                                    className="w-16 h-10 text-center rounded-lg border-2 border-gray-200 dark:border-gray-700 p-1 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    min="0"
                                    inputMode="numeric"
                                  />
                                  <button
                                    onClick={() => {
                                      handleSpecCountChange(
                                        spec.type,
                                        spec.count + 1,
                                      );
                                    }}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-rose-100 dark:hover:bg-rose-900/20 hover:text-rose-600 dark:hover:text-rose-400 transition-colors touch-manipulation"
                                  >
                                    <span className="text-lg font-medium">
                                      +
                                    </span>
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  variant="solid"
                  size="lg"
                  rounded="xl"
                  fullWidth
                  bgColor="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700"
                  textColor="text-white"
                  className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 mt-6"
                  loading={buttonLoading}
                  disabled={isFormDisabled()}
                  onClick={handleSubmit}
                  data-testid="create-interactive-material-button"
                >
                  {t("interactive_material.create.create")}
                </Button>
              </div>
            ) : (
              <LoadingContainer
                showSteps={getCreationSteps()}
                themeColor="rose"
                title={t("interactive_material.create.creating")}
                duration={30}
                onLastStepNearCompletion={() => {
                  if (newMaterialId) {
                    queryClient.invalidateQueries({
                      queryKey: ["interactiveMaterial", newMaterialId],
                    });
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      <FileUploadModal
        isOpen={showUploadModal}
        onClose={handleUploadModalClose}
        onUploadSuccess={handleUploadModalSuccess}
        onUploadError={handleUploadModalError}
        acceptedFileTypes={[".pdf"]}
        maxFileSize={10}
      />
    </div>
  );
};

export default CreateInteractiveMaterialView;
